import { enqueueSnackbar } from "notistack"
import { useField } from "formik"
import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { selectEffectiveUserType } from "../../../../../../modules/context/contextSelectors"
import { UserType } from "../../../../../../modules/login/loginTypes"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { MonetaryAmount } from "../../../../../../modules/currency/currencyTypes"
import { toDinero, toNumberLossy } from "../../../../../../modules/currency/currencyLib"
import { StandardDialog } from "../../../../dialogs/StandardDialog"

import {
    type ProductWishOrderLine,
    DeliveryOrderLine,
    DeliveryTimesField,
    DynamicConfigKey,
    isProductWishOrderLine,
} from "../../form/formTypes"
import { ProductWishForm, ProductWishFormValues } from "./ProductWishForm"

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "0 8rem 3rem",
    },
    message: {
        color: theme.palette.tailwind.blue[950],
        background: theme.palette.tailwind.sky[100],
        borderRadius: ".5rem",
        padding: "1rem 2rem",
        fontWeight: "500",
    },
}))

export type ProductWishItem = Readonly<{
    name: string
    quantity: number
    price?: MonetaryAmount
}>

// A wish line is identified only by this id (delete / edit price / React key), so it must stay unique
// for the lifetime of the dialog. Deriving it from the line count made "delete a line, then add a wish
// with the same name" produce a duplicate id, which then deleted or repriced both lines at once.
// The prefix covers a module re-evaluation (dev hot reload keeps the form state but resets the
// counter), so a line added afterwards can never reuse the id of one already in the cart.
const productWishLineIdPrefix = Math.random().toString(36).slice(2, 8)
let productWishLineCounter = 0

function createProductWishLineId(activeTime: string): string {
    productWishLineCounter += 1
    return `wish_${productWishLineIdPrefix}_${productWishLineCounter}_${activeTime}`
}

export type EditingWish = Readonly<{
    deliveryTime: string
    line: ProductWishOrderLine
}>

type ProductWishDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    editingWish?: EditingWish
}>

export function ProductWishDialog({ open, onClose, editingWish }: ProductWishDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const effectiveUserType = useAppSelector(selectEffectiveUserType)
    const isUserType = effectiveUserType === UserType.USER
    const isEditMode = Boolean(editingWish)

    const addTitleKey = isUserType ? "create-order-wish-dialog-title" : "create-order-order-line-dialog-title"
    const addTitle = isUserType ? "Add product wish" : "Add product"
    const dialogTitleKey = isEditMode ? "create-order-wish-dialog-edit-price-title" : addTitleKey
    const dialogTitle = isEditMode ? "Set price" : addTitle

    const editInitialValues: ProductWishFormValues | undefined = editingWish
        ? {
              name: editingWish.line.name,
              quantity: String(editingWish.line.quantity),
              price: editingWish.line.price ? toNumberLossy(toDinero(editingWish.line.price)) : undefined,
          }
        : undefined

    const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const handleEditProductWish = (item: ProductWishItem) => {
        if (!editingWish) return
        const updatedDeliveries = {
            ...deliveries,
            deliveryTimes: deliveries.deliveryTimes.map((delivery) => {
                if (delivery.time !== editingWish.deliveryTime) return delivery
                return {
                    ...delivery,
                    orderLines: delivery.orderLines.map(
                        (line): DeliveryOrderLine =>
                            isProductWishOrderLine(line) && line.id === editingWish.line.id
                                ? { ...line, name: item.name, quantity: item.quantity, price: item.price }
                                : line,
                    ),
                }
            }),
        }

        deliveriesHelpers.setValue(updatedDeliveries)
        onClose()
    }

    const handleAddProductWish = (item: ProductWishItem) => {
        const activeTime = deliveries.activeTime
        if (!activeTime) {
            enqueueSnackbar(l10n.getString("create-order-delivery-times-empty-error"), { variant: "error" })
            return
        }
        const updatedDeliveries = {
            ...deliveries,
            deliveryTimes: deliveries.deliveryTimes.map((delivery) => {
                if (delivery.time === activeTime) {
                    const wishLine: ProductWishOrderLine = {
                        type: DynamicConfigKey.PRODUCT_WISH,
                        id: createProductWishLineId(activeTime),
                        ...item,
                    }
                    const newOrderLines = [...delivery.orderLines, wishLine]

                    return { ...delivery, orderLines: newOrderLines }
                }
                return delivery
            }),
        }

        deliveriesHelpers.setValue(updatedDeliveries)
        onClose()
    }

    return (
        <LocalizedStrict id={dialogTitleKey} attrs={{ dialogTitle: true }}>
            <StandardDialog
                dialogTitle={dialogTitle}
                open={open}
                onClose={onClose}
                maxWidthProp="md"
                scroll="body"
                slotProps={{ root: { sx: { zIndex: 9999 } } }}
            >
                <Stack gap="1.5rem" className={classes.container}>
                    {isUserType && !isEditMode && (
                        <LocalizedStrict id="create-order-wish-dialog-message">
                            <Typography variant="body1" className={classes.message}>
                                If you add a product wish, your order needs to be manually approved by the merchant.
                            </Typography>
                        </LocalizedStrict>
                    )}
                    <ProductWishForm
                        onSubmit={isEditMode ? handleEditProductWish : handleAddProductWish}
                        initialValues={editInitialValues}
                    />
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
